import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useChatbot } from '@/context/ChatbotContext';
import {
  MessageCircle,
  X,
  Send,
  Calendar,
  Phone,
  ArrowLeft,
  CheckCircle,
  Loader2,
  User
} from 'lucide-react';
import { toast } from 'sonner';

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function Chatbot() {
  const { isOpen, setIsOpen, activeTab, setActiveTab } = useChatbot();
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [customerInfo, setCustomerInfo] = useState({ name: '', email: '', phone: '' });
  const [showCustomerForm, setShowCustomerForm] = useState(true);
  const messagesEndRef = useRef(null);

  // Booking form state
  const [bookingData, setBookingData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
    topic: ''
  });

  const WHATSAPP_NUMBER = '+60175946661';

  // Load conversation from localStorage
  useEffect(() => {
    const savedConvId = localStorage.getItem('chatConversationId');
    const savedCustomerInfo = localStorage.getItem('chatCustomerInfo');
    
    if (savedConvId) {
      setConversationId(savedConvId);
      setShowCustomerForm(false);
    }
    if (savedCustomerInfo) {
      setCustomerInfo(JSON.parse(savedCustomerInfo));
    }
  }, []);

  // Fetch messages when conversation is active
  useEffect(() => {
    if (conversationId && activeTab === 'chat') {
      fetchMessages();
      const interval = setInterval(fetchMessages, 3000); // Poll every 3 seconds
      return () => clearInterval(interval);
    }
  }, [conversationId, activeTab]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    if (!conversationId) return;
    try {
      const response = await axios.get(`${API}/conversations/${conversationId}/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const startConversation = async (e) => {
    e.preventDefault();
    if (!customerInfo.name) {
      toast.error('Please enter your name');
      return;
    }

    setLoading(true);
    try {
      // Map frontend field names to API expected field names
      const payload = {
        customer_name: customerInfo.name,
        customer_email: customerInfo.email || null,
        customer_phone: customerInfo.phone || null
      };
      const response = await axios.post(`${API}/conversations`, payload);
      const convId = response.data.id;
      setConversationId(convId);
      localStorage.setItem('chatConversationId', convId);
      localStorage.setItem('chatCustomerInfo', JSON.stringify(customerInfo));
      setShowCustomerForm(false);
      setActiveTab('chat');
    } catch (error) {
      console.error('Chat start error:', error);
      toast.error('Failed to start conversation');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !conversationId) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    
    // Optimistically add message to UI
    const tempMessage = {
      id: Date.now().toString(),
      conversation_id: conversationId,
      sender: 'customer',
      message: messageText,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempMessage]);

    try {
      await axios.post(`${API}/conversations/${conversationId}/messages`, {
        message: messageText
      });
    } catch (error) {
      toast.error('Failed to send message');
      // Remove optimistic message on error
      setMessages(prev => prev.filter(m => m.id !== tempMessage.id));
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    if (!bookingData.name || !bookingData.email || !bookingData.date || !bookingData.time) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: bookingData.name,
        email: bookingData.email,
        phone: bookingData.phone,
        subject: 'Meeting Booking Request',
        message: `Booking Request:\n\nDate: ${bookingData.date}\nTime: ${bookingData.time}\nTopic: ${bookingData.topic || 'General Discussion'}`
      });
      setActiveTab('success');
      setBookingData({ name: '', email: '', phone: '', date: '', time: '', topic: '' });
    } catch (error) {
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openWhatsApp = () => {
    const message = encodeURIComponent('Hi! I would like to know more about your services.');
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${message}`, '_blank');
  };

  const resetChat = () => {
    setActiveTab('menu');
  };

  const startNewChat = () => {
    localStorage.removeItem('chatConversationId');
    setConversationId(null);
    setMessages([]);
    setShowCustomerForm(true);
    setActiveTab('chat');
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Chat Button with Label */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-center gap-2">
        {/* Talk to Human Label - only show when chat is closed */}
        {!isOpen && (
          <div className="bg-white dark:bg-slate-800 px-4 py-2 rounded-full shadow-lg animate-bounce-gentle">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap flex items-center gap-1">
              Talk to Human 😊
            </span>
          </div>
        )}
        
        {/* Chat Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
            isOpen ? 'bg-slate-700 rotate-0' : 'bg-blue-600 hover:bg-blue-700 hover:scale-110'
          }`}
        >
          {isOpen ? (
            <X className="w-6 h-6 text-white" />
          ) : (
            <MessageCircle className="w-6 h-6 text-white" />
          )}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 z-50 w-[360px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white">
            <div className="flex items-center gap-3">
              {activeTab !== 'menu' && activeTab !== 'success' && (
                <button onClick={resetChat} className="hover:bg-white/20 p-1 rounded-full transition-colors">
                  <ArrowLeft className="w-5 h-5" />
                </button>
              )}
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-lg font-bold">A</span>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold">Aurous Consultancy</h3>
                <p className="text-xs text-blue-100">
                  {activeTab === 'chat' ? (
                    <span className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                      Online - We reply instantly
                    </span>
                  ) : (
                    'We typically reply within minutes'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="h-[400px] overflow-y-auto">
            {/* Main Menu */}
            {activeTab === 'menu' && (
              <div className="p-4 space-y-3">
                <p className="text-slate-600 text-sm mb-4">
                  Hi there! 👋 How can we help you today?
                </p>
                
                <button
                  onClick={() => setActiveTab('chat')}
                  className="w-full flex items-center gap-3 p-4 bg-blue-50 hover:bg-blue-100 rounded-xl transition-colors text-left border-2 border-blue-200"
                >
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
                    <MessageCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Live Chat</p>
                    <p className="text-xs text-slate-500">Chat with our sales team in real-time</p>
                  </div>
                </button>

                <button
                  onClick={() => setActiveTab('booking')}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Book a Meeting</p>
                    <p className="text-xs text-slate-500">Schedule a consultation call</p>
                  </div>
                </button>

                <button
                  onClick={openWhatsApp}
                  className="w-full flex items-center gap-3 p-4 bg-green-50 hover:bg-green-100 rounded-xl transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                    <WhatsAppIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Chat on WhatsApp</p>
                    <p className="text-xs text-slate-500">Quick response via WhatsApp</p>
                  </div>
                </button>

                <button
                  onClick={() => window.location.href = `tel:${WHATSAPP_NUMBER}`}
                  className="w-full flex items-center gap-3 p-4 bg-slate-50 hover:bg-slate-100 rounded-xl transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Call Us</p>
                    <p className="text-xs text-slate-500">{WHATSAPP_NUMBER}</p>
                  </div>
                </button>
              </div>
            )}

            {/* Live Chat */}
            {activeTab === 'chat' && (
              <div className="flex flex-col h-full">
                {showCustomerForm ? (
                  // Customer Info Form
                  <div className="p-4 space-y-4">
                    <p className="text-slate-600 text-sm">
                      Please provide your details to start chatting with our team.
                    </p>
                    
                    <form onSubmit={startConversation} className="space-y-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
                        <Input
                          placeholder="Your name"
                          value={customerInfo.name}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Email</label>
                        <Input
                          type="email"
                          placeholder="your@email.com"
                          value={customerInfo.email}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
                        <Input
                          placeholder="+60 12-345 6789"
                          value={customerInfo.phone}
                          onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                          className="h-10"
                        />
                      </div>
                      <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                        {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageCircle className="w-4 h-4 mr-2" />}
                        Start Chat
                      </Button>
                    </form>
                  </div>
                ) : (
                  // Chat Messages
                  <>
                    <div className="flex-1 p-4 space-y-3 overflow-y-auto">
                      {/* Welcome Message */}
                      <div className="flex gap-2">
                        <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-xs font-bold">A</span>
                        </div>
                        <div className="bg-slate-100 rounded-2xl rounded-tl-sm px-4 py-2 max-w-[80%]">
                          <p className="text-sm text-slate-700">
                            Hi {customerInfo.name}! 👋 Welcome to Aurous Consultancy. How can we help you today?
                          </p>
                          <p className="text-xs text-slate-400 mt-1">Aurous Team</p>
                        </div>
                      </div>

                      {/* Messages */}
                      {messages.map((msg) => (
                        <div 
                          key={msg.id} 
                          className={`flex gap-2 ${msg.sender === 'customer' ? 'flex-row-reverse' : ''}`}
                        >
                          {msg.sender === 'admin' && (
                            <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center flex-shrink-0" title={msg.admin_name || 'Team Member'}>
                              <span className="text-white text-xs font-bold">{(msg.admin_name || 'A').charAt(0).toUpperCase()}</span>
                            </div>
                          )}
                          <div className={`rounded-2xl px-4 py-2 max-w-[80%] ${
                            msg.sender === 'customer' 
                              ? 'bg-blue-600 text-white rounded-tr-sm' 
                              : 'bg-slate-100 text-slate-700 rounded-tl-sm'
                          }`}>
                            <p className="text-sm">{msg.message}</p>
                            <p className={`text-xs mt-1 ${msg.sender === 'customer' ? 'text-blue-200' : 'text-slate-400'}`}>
                              {msg.admin_name && msg.sender === 'admin' && <span className="font-medium text-slate-600">{msg.admin_name} • </span>}
                              {formatTime(msg.created_at)}
                            </p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <form onSubmit={sendMessage} className="p-3 border-t bg-slate-50">
                      <div className="flex gap-2">
                        <Input
                          placeholder="Type a message..."
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          className="flex-1 h-10 bg-white"
                        />
                        <Button type="submit" size="icon" className="h-10 w-10 bg-blue-600 hover:bg-blue-700">
                          <Send className="w-4 h-4" />
                        </Button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            )}

            {/* Booking Form */}
            {activeTab === 'booking' && (
              <form onSubmit={handleBooking} className="p-4 space-y-4">
                <p className="text-slate-600 text-sm mb-2">
                  Schedule a consultation with our team.
                </p>
                
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Name *</label>
                  <Input
                    placeholder="Your name"
                    value={bookingData.name}
                    onChange={(e) => setBookingData({ ...bookingData, name: e.target.value })}
                    className="h-10"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Email *</label>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={bookingData.email}
                    onChange={(e) => setBookingData({ ...bookingData, email: e.target.value })}
                    className="h-10"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
                  <Input
                    placeholder="+60 12-345 6789"
                    value={bookingData.phone}
                    onChange={(e) => setBookingData({ ...bookingData, phone: e.target.value })}
                    className="h-10"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Date *</label>
                    <Input
                      type="date"
                      value={bookingData.date}
                      onChange={(e) => setBookingData({ ...bookingData, date: e.target.value })}
                      className="h-10"
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">Time *</label>
                    <Input
                      type="time"
                      value={bookingData.time}
                      onChange={(e) => setBookingData({ ...bookingData, time: e.target.value })}
                      className="h-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Topic</label>
                  <Input
                    placeholder="What would you like to discuss?"
                    value={bookingData.topic}
                    onChange={(e) => setBookingData({ ...bookingData, topic: e.target.value })}
                    className="h-10"
                  />
                </div>

                <Button type="submit" className="w-full bg-purple-600 hover:bg-purple-700" disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Book Meeting
                </Button>
              </form>
            )}

            {/* Success State */}
            {activeTab === 'success' && (
              <div className="p-4 text-center py-12">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Thank You!</h3>
                <p className="text-slate-600 text-sm mb-6">
                  We have received your request and will get back to you shortly.
                </p>
                <Button onClick={resetChat} variant="outline" className="w-full">
                  Back to Menu
                </Button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-slate-50 border-t text-center">
            <p className="text-xs text-slate-500">
              Powered by Aurous Consultancy
            </p>
          </div>
        </div>
      )}
    </>
  );
}
